export const PENDING_MFA_STORAGE_KEY = "pendingMfa";
export const MFA_COOLDOWN_STORAGE_KEY = "mfaCooldowns";

export interface PendingMfaState {
  otpId: string;
  userId: number;
  email: string | null;
  maskedEmail: string | null;
  redirectUrl: string | null;
}

export interface MfaCooldownState {
  userId: number | null;
  email: string | null;
  startedAt: number;
  durationSeconds: number;
}

type StoredMfaCooldowns = Record<string, MfaCooldownState>;

export function normalizeMfaEmail(email: string | null | undefined) {
  const normalizedEmail = (email || "").trim().toLowerCase();

  return normalizedEmail || null;
}

export function readMfaCooldown(
  userId?: number | null,
  email?: string | null,
): MfaCooldownState | null {
  const cooldowns = readStoredMfaCooldowns();

  for (const key of buildMfaCooldownKeys(userId, email)) {
    if (cooldowns[key]) {
      return cooldowns[key];
    }
  }

  return null;
}

export function writeMfaCooldown(state: MfaCooldownState) {
  const cooldowns = readStoredMfaCooldowns();
  const normalizedState: MfaCooldownState = {
    ...state,
    userId: typeof state.userId === "number" ? state.userId : null,
    email: normalizeMfaEmail(state.email),
  };

  for (const key of buildMfaCooldownKeys(
    normalizedState.userId,
    normalizedState.email,
  )) {
    cooldowns[key] = normalizedState;
  }

  sessionStorage.setItem(MFA_COOLDOWN_STORAGE_KEY, JSON.stringify(cooldowns));
}

export function clearMfaCooldown(userId?: number | null, email?: string | null) {
  const cooldowns = readStoredMfaCooldowns();
  let changed = false;

  for (const key of buildMfaCooldownKeys(userId, email)) {
    if (cooldowns[key]) {
      delete cooldowns[key];
      changed = true;
    }
  }

  if (!changed) {
    return;
  }

  if (Object.keys(cooldowns).length === 0) {
    sessionStorage.removeItem(MFA_COOLDOWN_STORAGE_KEY);
    return;
  }

  sessionStorage.setItem(MFA_COOLDOWN_STORAGE_KEY, JSON.stringify(cooldowns));
}

function readStoredMfaCooldowns(): StoredMfaCooldowns {
  const value = sessionStorage.getItem(MFA_COOLDOWN_STORAGE_KEY);

  if (!value) {
    return {};
  }

  try {
    const parsedValue = JSON.parse(value) as StoredMfaCooldowns;

    return parsedValue && typeof parsedValue === "object" ? parsedValue : {};
  } catch {
    sessionStorage.removeItem(MFA_COOLDOWN_STORAGE_KEY);
    return {};
  }
}

function buildMfaCooldownKeys(
  userId?: number | null,
  email?: string | null,
) {
  const keys: string[] = [];
  const normalizedEmail = normalizeMfaEmail(email);

  if (typeof userId === "number" && userId > 0) {
    keys.push(`user:${userId}`);
  }

  if (normalizedEmail) {
    keys.push(`email:${normalizedEmail}`);
  }

  return keys;
}
